import type { FmReportPageTypedProps } from './FmReportPageTyped';
import type { FmElement } from './elements/elements.types';
import type {
  FmStoryLiveMapFigure,
  FmStoryPrimaryLiveMapFigure,
} from './elements/elementMedia.types';
import type { FmSection } from './sections/section.types';
import { validateSection } from './sections/section.validation';
import { isPrimaryMapSlotKey } from '@/shared/config/mapSlotRegistry';

/**
 * Preview projection shared by the visual-grammar gallery and Storybook: a
 * live primary map slot renders its preview state with no map data, so a
 * specimen never mounts the live map engine or fetches.
 */
function isPrimaryFigure(figure: FmStoryLiveMapFigure): figure is FmStoryPrimaryLiveMapFigure {
  return isPrimaryMapSlotKey(figure.slotKey);
}

function previewElement(element: FmElement): FmElement {
  if (element.element !== 'map') return element;
  const { figure } = element;
  if (figure?.kind !== 'live' || !isPrimaryFigure(figure)) return element;
  return {
    ...element,
    figure: { ...figure, slotProps: { ...figure.slotProps, preview: true, mapData: null } },
  };
}

function withPreviewMapState(section: FmSection): FmSection {
  if (!('slots' in section) || !section.slots) return section;
  const slots = Object.fromEntries(
    Object.entries(section.slots).map(([slot, elements]: [string, readonly FmElement[]]) => [
      slot,
      elements.map(previewElement),
    ]),
  );
  const candidate: unknown = { ...section, slots };
  validateSection(candidate, `preview section "${section.id}"`);
  return candidate;
}

export function withPreviewMapStates(descriptor: FmReportPageTypedProps): FmReportPageTypedProps {
  return {
    ...descriptor,
    chapters: descriptor.chapters.map((chapter) => ({
      ...chapter,
      sections: chapter.sections.map(({ role, section }) => ({
        role,
        section: withPreviewMapState(section),
      })),
    })),
  };
}
